import { useTranslation } from 'react-i18next';
import { isLocale, setLocale, type Locale } from '@/i18n';
import { useAuth } from '@/features/auth/AuthProvider';
import { useUpdateProfile } from './queries';

/**
 * Changing the language, in the one place that knows it has two jobs.
 *
 * The switch has to change this browser *and* the profile: the profile's locale
 * is what the server reads to send the weekly reminder, so a change that only
 * touched the browser meant the mail stayed in whatever language the account was
 * created in, forever and silently.
 *
 * It exists because there are now two switches — the select on desktop and a
 * menu item on a phone — and the second one would have been written without the
 * save, which is exactly how the bug came back the first time.
 */
export function useLocalePreference() {
  const { i18n } = useTranslation();
  const auth = useAuth();
  const save = useUpdateProfile();
  const current: Locale = isLocale(i18n.language) ? i18n.language : 'en';

  const change = (locale: Locale) => {
    setLocale(locale);
    // Best effort, and only when signed in: the same switch sits on the
    // marketing pages, where there is no profile to write to.
    if (auth.status === 'signedIn') save.mutate({ locale }, { onError: () => undefined });
  };

  return { current, change, other: current === 'es' ? ('en' as const) : ('es' as const) };
}

export const LOCALE_NAMES: Record<Locale, string> = { en: 'English', es: 'Español' };
